import { CapabilityCategory } from '@trimble/web3d-capabilities';
import { groupBy } from 'lodash';
import { createEffect, createMemo, getOwner, on, runWithOwner } from 'solid-js';
import { CapabilityCoordinatorBase } from './capability-coordinator-base';
import { CapabilityRegistryContainer } from './capability-registry/capability-registry-container';
import { detectArrayChanges } from '@trimble/utilities';

export class MutuallyExclusiveCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  constructor(registryContainer: CapabilityRegistryContainer) {
    super(registryContainer);

    const mutuallyExclusiveCapabilities = createMemo(() => {
      return groupBy(
        registryContainer
          .capabilitiesWithParent()
          .filter(
            (capability) => capability.category !== CapabilityCategory.Command
          ),
        'category'
      );
    });

    // Capture the reactive owner before creating child effects
    const owner = getOwner();

    // Only create effects for newly added capabilities
    createEffect(
      on(
        registryContainer.capabilitiesWithParent,
        (capabilities, prevCapabilities) => {
          // Detect only new capabilities
          const additions = detectArrayChanges(
            prevCapabilities,
            capabilities
          ).newItems;

          // Create an effect for each new capability with proper ownership
          additions.forEach((capability) => {
            runWithOwner(owner, () => {
              createEffect(
                on(capability.isActive, (active, prevActive) => {
                  if (!active || active === prevActive) {
                    return;
                  }

                  const exclusiveCapabilities =
                    mutuallyExclusiveCapabilities()[capability.category];

                  if (!exclusiveCapabilities) {
                    return;
                  }

                  const otherActiveCapabilities = exclusiveCapabilities.filter(
                    (c) => c !== capability && c.isActive()
                  );

                  otherActiveCapabilities.forEach((c) => c.deactivate());
                })
              );
            });
          });
        }
      )
    );
  }

  public cleanup(): Promise<void> {
    return Promise.resolve();
  }
}
